import { StatCard, Surface } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import type { ClaimStatsBucket } from '@warehouse/shared';
import { BarChart3, CheckCircle2, Clock, Layers, Scale } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { PageHeader } from '../../../components/PageHeader';
import { formatInteger, formatQty } from '../../../lib/format';
import { errorMessage } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { claimKeys, fetchClaimStats } from '../api/claims-api';
import { useClaimStatuses } from '../hooks/useClaimStatuses';

/**
 * Claim statistics (spec §5.11): per month, per reason, per restaurant, per
 * article.
 *
 * Two bar charts and two top-N tables — deliberately plain. A claims dashboard
 * is read at a glance by someone deciding which supplier or which restaurant to
 * call, so the chart's only job is to make the outlier obvious. Colours come
 * from the same status catalog the pills use, so nothing invents a palette.
 */
export function ClaimStatisticsPage(): ReactNode {
  const { t } = useTranslation(['claims', 'common']);
  const { color: statusColor, label: statusLabel } = useClaimStatuses();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const query = useQuery({
    queryKey: claimKeys.stats({ from, to }),
    queryFn: () => fetchClaimStats({ ...(from ? { from } : {}), ...(to ? { to } : {}) }),
  });
  useLiveQuery(['claims'], claimKeys.all);

  const stats = query.data;

  const monthData = (stats?.byMonth ?? []).map((bucket) => ({
    name: bucket.key,
    claims: bucket.claims,
  }));
  const reasonData = (stats?.byReason ?? []).map((bucket) => ({
    name: t(`claims:reason.${bucket.key}`, { defaultValue: bucket.key }),
    claims: bucket.claims,
  }));
  const statusData = (stats?.byStatus ?? []).map((bucket) => ({
    name: statusLabel(bucket.key as never),
    claims: bucket.claims,
    fill: statusColor(bucket.key as never),
  }));

  return (
    <div>
      <PageHeader
        title={t('claims:stats.title')}
        subtitle={t('claims:stats.subtitle')}
        actions={
          <>
            <label className="flex items-center gap-2 text-sm text-ink-light">
              {t('claims:stats.from')}
              <input
                type="date"
                className="rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-sm"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                }}
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-light">
              {t('claims:stats.to')}
              <input
                type="date"
                className="rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-sm"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                }}
              />
            </label>
          </>
        }
      />

      {query.error ? <p className="mb-4 text-danger">{errorMessage(query.error)}</p> : null}

      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          icon={<BarChart3 />}
          label={t('claims:stats.totals.claims')}
          value={formatInteger(stats?.totals.claims ?? 0)}
          loading={query.isLoading}
        />
        <StatCard
          icon={<Layers />}
          label={t('claims:stats.totals.lines')}
          value={formatInteger(stats?.totals.lines ?? 0)}
          loading={query.isLoading}
        />
        <StatCard
          icon={<Scale />}
          label={t('claims:stats.totals.qty')}
          value={formatQty(stats?.totals.qtyBase ?? '0')}
          loading={query.isLoading}
        />
        <StatCard
          icon={<Clock />}
          label={t('claims:stats.totals.open')}
          value={formatInteger(stats?.totals.open ?? 0)}
          loading={query.isLoading}
        />
        <StatCard
          icon={<CheckCircle2 />}
          label={t('claims:stats.totals.resolved')}
          value={formatInteger(stats?.totals.resolved ?? 0)}
          loading={query.isLoading}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Chart title={t('claims:stats.byMonth')} empty={monthData.length === 0} emptyText={t('claims:stats.empty')}>
          <BarChart data={monthData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-surface-border" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={32} />
            <Tooltip cursor={{ fillOpacity: 0.06 }} />
            <Bar dataKey="claims" name={t('claims:stats.chartClaims')} fill="#2563eb" radius={[4, 4, 0, 0]} />
          </BarChart>
        </Chart>

        <Chart title={t('claims:stats.byReason')} empty={reasonData.length === 0} emptyText={t('claims:stats.empty')}>
          <BarChart data={reasonData} layout="vertical" margin={{ left: 24 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="currentColor" className="text-surface-border" />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
            <Tooltip cursor={{ fillOpacity: 0.06 }} />
            <Bar dataKey="claims" name={t('claims:stats.chartClaims')} fill="#f59e0b" radius={[0, 4, 4, 0]} />
          </BarChart>
        </Chart>

        <Chart title={t('claims:stats.byStatus')} empty={statusData.length === 0} emptyText={t('claims:stats.empty')}>
          <BarChart data={statusData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-surface-border" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={32} />
            <Tooltip cursor={{ fillOpacity: 0.06 }} />
            <Bar dataKey="claims" name={t('claims:stats.chartClaims')} radius={[4, 4, 0, 0]}>
              {statusData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </Chart>

        <Surface variant="card">
          <h2 className="mb-3 font-display text-base text-ink">{t('claims:stats.byRestaurant')}</h2>
          <TopTable rows={stats?.byRestaurant ?? []} />
        </Surface>

        <Surface variant="card" className="lg:col-span-2">
          <h2 className="mb-3 font-display text-base text-ink">{t('claims:stats.byArticle')}</h2>
          <TopTable rows={stats?.byArticle ?? []} />
        </Surface>
      </div>
    </div>
  );
}

function Chart({
  title,
  empty,
  emptyText,
  children,
}: {
  title: string;
  empty: boolean;
  emptyText: string;
  children: ReactNode;
}): ReactNode {
  return (
    <Surface variant="card">
      <h2 className="mb-3 font-display text-base text-ink">{title}</h2>
      {empty ? (
        <p className="py-10 text-center text-sm text-ink-muted">{emptyText}</p>
      ) : (
        <div className="h-64" data-testid="claims-chart">
          <ResponsiveContainer width="100%" height="100%">
            {children as never}
          </ResponsiveContainer>
        </div>
      )}
    </Surface>
  );
}

function TopTable({ rows }: { rows: readonly ClaimStatsBucket[] }): ReactNode {
  const { t } = useTranslation('claims');
  if (rows.length === 0) return <p className="text-sm text-ink-muted">{t('stats.empty')}</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-surface-border text-left text-xs uppercase text-ink-muted">
            <th className="py-2 pr-3">{t('stats.columns.name')}</th>
            <th className="py-2 pr-3 text-right">{t('stats.columns.claims')}</th>
            <th className="py-2 pr-3 text-right">{t('stats.columns.lines')}</th>
            <th className="py-2 text-right">{t('stats.columns.qty')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-surface-border/60">
              <td className="py-2 pr-3">{row.label ?? row.key}</td>
              <td className="py-2 pr-3 text-right">{formatInteger(row.claims)}</td>
              <td className="py-2 pr-3 text-right">{formatInteger(row.lines)}</td>
              <td className="py-2 text-right">{formatQty(row.qtyBase)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
